import { areStringsInsensitivelyEqual, pick, capitalizeString } from 'shared/utils/utils';
import { Fake, substituteQueryWithFakes } from 'shared/utils/querySubstitution';
import { Type } from 'shared/utils/flowstep';


export const Separator = {
  Name: '.',
  Label: ' : ',
  MultiselectPicklist: ';',
};

export const ColumnType = {
  Field: 'field',
  Function: 'function',
  PolymorphicRelationshipType: 'poly-ref-type',
};

export const RelationshipType = {
  Relationship: 'relationship',
  ChildRelationship: 'child-relationship',
  Field: 'field',
};

export const KnownFunction = {
  Count: { name: 'count', argsCount: 0 },
  Other: { name: 'other' },
};

export const KnownFields = {
  Id: 'id',
  Name: 'name',
  Type: 'type',
  Subject: 'subject',
  CaseNumber: 'casenumber',
  Status: 'status',
  WorkOrderNumber: 'workordernumber'
};

export const KnownObjects = {
  Account: 'Account',
  Contact: 'Contact',
  Opportunity: 'Opportunity',
  Task: 'Task',
  Event: 'Event',
  Case: 'Case',
  Lead: 'Lead',
  Individual: 'Individual',
  WorkOrder: 'WorkOrder'
};


export const ParsingOption = {
  Fields:               1 << 0,
  Subqueries:           1 << 1,
  Functions:            1 << 2,
  Filters:              1 << 3,
  AllowUnknownObjects:  1 << 10,

  get Columns() {
    return this.Fields | this.Subqueries | this.Functions;
  },

  get All() {
    return this.Columns | this.Filters;
  }, 
};


export const FieldType = {
  Relationship: 'relationship',
  Picklist: 'picklist',
  Lookup: 'lookup',
  Generic: 'generic',
};

export const LookupType = {
  Id: 'id',
  Reference: 'ref',
  PolymorphicRef: 'poly-ref',
};

export const FieldTypeName = {
  Address: 'address',
  Date: 'date',
  Time: 'time',
  DateTime: 'datetime',
  Id: 'id',
  Reference: 'reference',
  Double: 'double',
  Int: 'int',
  Byte: 'byte',
  Boolean: 'boolean',
  Currency: 'currency',
  Percent: 'percent',
  String: 'string',
  Email: 'email',
  Phone: 'phone',
  Textarea: 'textarea',
  Url: 'url',
  Picklist: 'picklist',
  Multipicklist: 'multipicklist',
  RichText: 'rich'
};  


export const NumberFields = [
  FieldTypeName.Byte,
  FieldTypeName.Double,
  FieldTypeName.Int,
  FieldTypeName.Currency,
  FieldTypeName.Percent,
];

// SOSL
export const InFieldsType = {
  All: 'all',
  Email: 'email',
  Name: 'name',
  Phone: 'phone',
  Sidebar: 'sidebar',
};

const FieldsFunOptions = {
  Standard: 1 << 0,
  Custom:   1 << 1,
  get All() { 
    return this.Standard | this.Custom;
  } 
}


const toFull = (parentRelationshipNames, name, separator) => [...parentRelationshipNames, name].join(separator);
const isNotEmptyPicklistField = (field) => field.picklistValues && field.picklistValues.length > 0;

export const toFullName = (parentRelationships, name) => toFull(parentRelationships, name, Separator.Name);
export const toFullLabel = (parentRelationships, label) => toFull(parentRelationships, label, Separator.Label);
export const parseFullName = (fullFieldName) => fullFieldName.split(Separator.Name);

export const getRelationshipReferences = (field) => field.referenceTo ?? [];
export const getRelationshipName = (field) => field.relationshipName ?? '';
export const isRelationshipField = (field) => !!field.relationshipName && !!field.referenceTo;
export const isPolymorphicField = (field) => isRelationshipField(field) && field.referenceTo.length > 1; // See standard 'Task' object for example

// TODO: Add more for used fields' types
export const isSinglePicklistField = (field) => field.type === FieldTypeName.Picklist && isNotEmptyPicklistField(field);
export const isMultiselectPicklistField = (field) => field.type === FieldTypeName.Multipicklist && isNotEmptyPicklistField(field);
export const isPicklistField = (field) => isSinglePicklistField(field) || isMultiselectPicklistField(field);
export const isPicklistFieldWeak = (field) => field.type === FieldTypeName.Picklist || field.type === FieldTypeName.Multipicklist;
export const isRestrictedPicklistField = (field) => isPicklistField(field) && !!field.restrictedPicklist;

export const isIdField = (field) => field.type === FieldTypeName.Id;
export const isLookupField = (field) => isIdField(field) || isRelationshipField(field);
export const getLookupType = (field) => isIdField(field) 
  ? LookupType.Id
  : isPolymorphicField(field) ? LookupType.PolymorphicRef : LookupType.Reference;

export const isNumberField = (field) => NumberFields.includes(field.type);
export const isBooleanField = (field) => field.type === FieldTypeName.Boolean;

export const SalesforceIdRegexp = /^([a-z0-9]{15,18})$/i;
export const isSalesforceId = (value) => typeof(value) === 'string' && !!value.match(SalesforceIdRegexp);
export const isOldFormatSalesforceId = (value) => isSalesforceId(value) && value.length === 15;
export const isRichTextField = (field) => field.type === FieldTypeName.Textarea && field.htmlFormatted

export const convertOldFormatId = (value) => {
  let addon = '';
  const dict = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ012345';

  for (let iBlock = 0; iBlock < 3; ++iBlock) {
    let offset = 0;
    
    for (let iPosition = 0; iPosition < 5; ++iPosition) {
      const current = value.charAt(iBlock * 5 + iPosition);
      if (current>="A" && current<="Z") {
        offset += 1 << iPosition;
      }
    }

    addon += dict.charAt(offset);
  }

  return value + addon;
}

const typesInfo = [
  { fn: isPicklistField,        type: FieldType.Picklist },
  { fn: isRelationshipField,    type: FieldType.Relationship },
  { fn: isLookupField,          type: FieldType.Lookup },
];

export const toCanonicalForm = (fieldName) => {
  return fieldName.toLowerCase();
}

export const toFieldTypes = (field) => {
  const types = typesInfo.filter(x => x.fn(field)).map(x => x.type);
  return !!types.length ? types : [FieldType.Generic];
}

const SpecificObjects = {
  [toCanonicalForm(KnownObjects.Task)]: KnownFields.Subject,
  [toCanonicalForm(KnownObjects.Event)]: KnownFields.Subject,
  [toCanonicalForm(KnownObjects.Case)]: KnownFields.CaseNumber,
  [toCanonicalForm(KnownObjects.WorkOrder)] : KnownFields.WorkOrderNumber
};

export const getNameFieldName = (objectName) => {
  return SpecificObjects[toCanonicalForm(objectName)] ?? KnownFields.Name;
}

export const escapeValue = (value) => {
  return value.replace(/'/g, `\\'`);
}



export class RelationshipError extends Error {
  constructor(relationshipNames) {
    super(`Unable to extract data of '${relationshipNames.join(Separator.Name)}' relationship`);
    this.relationshipNames = relationshipNames;
  }
}


export async function parseSoqlQuery(query, { queryParser, objectLoader = null }, { options = ParsingOption.All, showLabels = false, useLoader = true }) {
  if (!options) {
    return { query, substitutedQuery: query };
  }

  let substitutedQuery = '';
  let trueObjectName = '';
  let ast = null;

  try {
    substitutedQuery = substituteQueryWithFakes(query, Type.Soql).query;
    ast = queryParser(substitutedQuery);
    const { sObject: objectName, where } = ast; 

    trueObjectName = objectLoader
      ? (await objectLoader(objectName)).name
      : objectName;

    const columns = options & ParsingOption.Columns
      ? await extractFields(ast, trueObjectName, objectLoader, { options, showLabels, useLoader })
      : [];
    
    const filters = (options & ParsingOption.Filters) && where
      ? await extractFilters(where, trueObjectName, objectLoader)
      : {};

    return { query, substitutedQuery, objectName: trueObjectName, columns, filters, ast };
  }
  catch (err) {
    if ((options & ParsingOption.AllowUnknownObjects) && err instanceof RelationshipError) {
      return { query, substitutedQuery, objectName: trueObjectName, columns: [], filters: {}, ast };
    }
    console.log('parseSoqlQuery::Parsing error: ', err.message);
    // console.log('parseQuery::Line: ', err.lineNumber, err.stack);
    throw err;
  }
}

// TODO:
function composeQuery(ast, parts = {}, composers = {}) {

}

export async function parseSoslQuery(query, { objectLoader }, { options = ParsingOption.Fields, showLabels = false }) {
  let trueObjectName = '';

  try {
    const { isValid, objectName, fields, ...rest } = extractSoslQueryData(query);
    if (!isValid) {
      throw new Error('Unable to parse SOSL query: either FIND or RETURNING statement is missing or ill-formed');
    }

    trueObjectName = objectLoader
      ? (await objectLoader(objectName)).name
      : objectName;

    const fakeAst = generateFakeAstForSosl(fields);
    // console.log('Fake AST: ', fakeAst);
    const columns = await extractFields(fakeAst, trueObjectName, objectLoader, { options, showLabels });

    return { objectName: trueObjectName, columns, ...rest };
  }
  catch (err) {
    console.log('parseSoslQuery::Parsing error: ', err.message);
    // console.log('parseQuery::Line: ', err.lineNumber, err.stack);
    throw err;
  }
}

export function extractSoslQueryData(query = '') {
  // See SOSL syntax: 
  // https://developer.salesforce.com/docs/atlas.en-us.soql_sosl.meta/soql_sosl/sforce_api_calls_sosl_syntax.htm
  const findRegEx = /FIND\s*?{(?<findExpression>.*?)}(?!})/i;
  const returningRegEx = /RETURNING\s+(?<returningExpression>(?<objectName>\w+)\s*?(?:\((?<returnParams>.*?)\))?)/i;
  const fieldsRegEx = /(?:LIMIT|ORDER|OFFSET|WHERE|USING)\s+|$/i;
  const inRegEx = /\s+IN\s+(?<fieldsType>\w+)\s+FIELDS\s+/i;
  // const limitRegEx = /LIMIT\s+(?<limit_value>\d+)/i;

  const { objectName, returnParams, returningExpression } = returningRegEx.exec(query)?.groups ?? {};
  // console.log('Parse 1: ', objectName, returnParams, returningExpression);
  if (!objectName) {
    return { isValid: false };
  }

  const { findExpression } = findRegEx.exec(query)?.groups ?? {};
  if (!findExpression) {
    return { isValid: false };
  }

  let fields = [KnownFields.Id];
  if (returnParams) {
    const { index } = fieldsRegEx.exec(returnParams);
    fields = returnParams.slice(0, index).split(', ').map(x => x.trim());
  }

  const { fieldsType } = inRegEx.exec(query)?.groups ?? {};
  // console.log('Parse2: ', fieldsType);
  const inFieldType = fieldsType
    ? InFieldsType[capitalizeString(fieldsType.toLowerCase())] ?? InFieldsType.All
    : InFieldsType.All;


  return { isValid: true, objectName, fields, inFieldType, returningExpression, findExpression };
}

function generateFakeAstForSosl(fieldNames) {
  const fields = fieldNames.map(rawValue => {
    const rels = rawValue.split(Separator.Name);
    const relationships = rels.slice(0, rels.length - 1);
    const field = rels[rels.length - 1];
    return {
      type: 'Field',
      rawValue,
      relationships,
      field,
    };
  });

  return { fields };
}


function hasAggregationFields(astFields) {
  return astFields.some(x => x.type === 'FieldFunctionExpression'
    ? x.isAggregateFn
    : x.type === 'FieldSubquery' && hasAggregationFields(x.subquery.fields));
}

function isFieldFunctionExpr(astField) {
  return astField.type === 'FieldFunctionExpression';
}

export function isFieldsFunction(astField) {
  return isFieldFunctionExpr(astField) && astField.functionName === 'FIELDS';
}

function astFieldToRelationships(astField, isFieldFunc) {
  return { 
    fieldRelationshipNames: isFieldFunc
      ? astField.parameters[0].split(Separator.Name)
      : [...(astField.relationships ?? []), astField.field],
    fieldRawValue: isFieldFunc ? astField.rawValue : astField.field,
  };
}

function filterObjectFields(fields, filterOption = FieldsFunOptions.All) {
  return fields.reduce((res, field, fieldIndex) => {
    const fieldType = field.custom ? FieldsFunOptions.Custom : FieldsFunOptions.Standard;
    if (fieldType & filterOption) {
      res.push({field, fieldIndex });
    }
    return res;
  }, []);
}

async function extractFields(ast, parent, objectLoader, { options, showLabels = false, useLoader = true }) {
  const isAggregatedQuery = hasAggregationFields(ast.fields);
  const columns = [];

  let columnCounter = 0;
  let functionCounter = 0;
  //we need objectLoader for FIELDS function case. But for all other cases it can be nullable.
  const trueObjectLoader = useLoader ? objectLoader : null;

  const extractFieldsImpl = async (queryAst, objectName, parentRelationships = [], currentOptions = options) => {
    const parentRelationshipNames = parentRelationships.map(x => x.name);
    const { fields: astFields } = queryAst;
    for (const astField of astFields) {
      const { type, alias, rawValue, functionName } = astField;
      const isFieldFunc = isFieldFunctionExpr(astField);
      const isLabelFunc = isFieldFunc && functionName === 'toLabel';
      const isFeildsFunc = isFieldsFunction(astField);
      
      if (currentOptions & ParsingOption.Fields) {
        if (('Field' === type || 'FieldRelationship' === type || isLabelFunc)) {
          const { fieldRelationshipNames, fieldRawValue } = astFieldToRelationships(astField, isFieldFunc);
          const relationshipData = await loadFieldRelationshipOrThrow({
            field: fieldRelationshipNames,
            parent: parentRelationshipNames,
            type: RelationshipType.Field
          }, objectName, trueObjectLoader);

          const field = relationshipData[relationshipData.length - 1];

          const relationships = [...parentRelationships, ...relationshipData];
          const fullName = toFullName(field.parentRelationshipNames, field.name);
          const fullLabel = toFullLabel(field.parentRelationshipNames, field.label ?? field.name);
          const fullPath = fullName.split(Separator.Name);
          const name = isLabelFunc ? fieldRawValue : fullName;
          const label = isLabelFunc ? `${astField.functionName}(${fullLabel})` : fullLabel;
          const title = isAggregatedQuery && alias
            ? alias
            : showLabels ? label : name;
          const apiName = (isAggregatedQuery
            ? alias ?? fieldRawValue
            : fullName).toLowerCase();
          
          columns.push({
            index: columnCounter++,
            type: field.columnType,
            apiName, name, label, title,
            relationships,
            objectName: field.objectName,
            rawField: field.rawField,
            isInSubquery: !!relationships.find(x => x.type === RelationshipType.ChildRelationship),
            fullPath,
          });
          continue;
        }
        else if (isFeildsFunc) {
          const { parameters } = astField;
          const obj = await objectLoader(objectName);
          const fieldsParam = capitalizeString(parameters[0].toLowerCase());
          const filteredFields = filterObjectFields(obj.fields, FieldsFunOptions[fieldsParam]);
          columns.push(...filteredFields.map(({ field, fieldIndex }) => ({
            index: columnCounter++,
            type: ColumnType.Field,
            apiName: field.name.toLowerCase(),
            name: field.name,
            label: field.label,
            title: field.name,
            fullPath: [field.name],
            relationships: [{ columnType: ColumnType.Field, fieldIndex, parentRelationshipNames: [], rawFields: [], childRelationships: [] }],
            objectName: field.objectName,
            rawField: field,
            isInSubquery: false,
          })));
        }
      }
      
      if (isFieldFunc && !isLabelFunc && !isFeildsFunc && (currentOptions & ParsingOption.Functions)) {
        const value = alias || rawValue;
        const name = toFullName(parentRelationshipNames, value);
        const label = toFullLabel(parentRelationshipNames, value);
        const apiName = toFullName(parentRelationshipNames, alias || `expr${functionCounter++}`).toLowerCase();

        columns.push({ 
          index: columnCounter++, type: ColumnType.Function,
          apiName, name, label, title: name,
          functionType: Object.values(KnownFunction).find(
            x => areStringsInsensitivelyEqual(x.name, astField.functionName)
          ) ?? KnownFunction.Other
        });
        continue;
      }
  
      if ('FieldSubquery' === type) {
        const { subquery } = astField;
        const fieldRelationshipNames = [...(subquery.sObjectPrefix ?? []), subquery.relationshipName]; 
        const relationshipData = await loadFieldRelationshipOrThrow({ 
          field: fieldRelationshipNames, 
          parent: parentRelationshipNames, 
          type: RelationshipType.ChildRelationship 
        }, objectName, trueObjectLoader);
        
        // In case of disabled parsing of subqueries for correct counting of columns we need
        // to iterate through all the fields of the subqueries, but not add them to the columns
        const extractOptions = (currentOptions & ParsingOption.Subqueries)
          ? currentOptions
          : (currentOptions & ~ParsingOption.All) | ParsingOption.Subqueries;

        const { referenceTo } = relationshipData[relationshipData.length - 1];
        await extractFieldsImpl(subquery, referenceTo, [...parentRelationships, ...relationshipData], extractOptions);
        continue;
      }
    }

    return columns;
  }

  return await extractFieldsImpl(ast, parent);
}

// TODO: support substitutions
async function extractFilters(astWhere, objectName, objectLoader) {
  const expressions = [];
  let areFlat = true;
  let areAllOperatorsParsed = true;

  function cleanValue(value) {
    if (Array.isArray(value)) return value.map(x => cleanValue(x));
    if (typeof(value) === 'string' && Number.isNaN(+value)
        && value.startsWith("'") && value.endsWith("'") && value.length >= 2) {
      return value.slice(1, -1).replace(/\\'/g, "'");
    }
    return value;
  }

  function isFakeValue(value) {
    return !Array.isArray(value) &&
      (Number.isNaN(+value) ? value.startsWith(Fake.Pattern) : +value === Fake.UInt);  
  }

  async function toFilter(astNode, join = '') {
    const { field: fieldName, openParen, closeParen, valueQuery } = astNode;
    
    if (valueQuery || !fieldName) {
      areAllOperatorsParsed = false;
      return;
    }
    
    const value = cleanValue(astNode.value);
    const isSubstitution = isFakeValue(value);
    const operator = astNode.operator.toLowerCase();
    const fieldRelationshipNames = parseFullName(fieldName);
    const fieldRelationships = await loadFieldRelationshipOrThrow({
      field: fieldRelationshipNames, type: RelationshipType.Field
    }, objectName, objectLoader);

    if ((openParen || closeParen) && openParen !== closeParen) {
      areFlat = false;
    }

    expressions.push({ fieldRelationships, operator, value, join, isSubstitution });
  } 

  async function parse(astNode, join = '') {
    if (join === 'not') {
      areAllOperatorsParsed = false;
      return;
    } 

    const { left, right, operator } = astNode;
    await toFilter(left, join);
    right && await parse(right, operator?.toLowerCase() ?? '');
  }

  await parse(astWhere);

  return { expressions, areAllOperatorsParsed, areFlat };
}

async function loadFieldRelationshipOrThrow(relationships, objectName, objectLoader = null) {
  const result = await loadFieldRelationship(relationships, objectName, objectLoader);
  if (!result) {
    const { field, parent = [] } = relationships;
    throw new RelationshipError([...parent, ...field]);
  }
  return result;
}

async function loadFieldRelationship(relationships, objectName, objectLoader = null) {
  const { field: fRelationships, parent: pRelationships = [], type: relationshipType } = relationships;

  // SELECT Contact.Name from Contact - ignore object name
  // SELECT Id, (SELECT Id FROM Account.Contacts) FROM Account -  -//-
  const fieldRelationshipNames = fRelationships.length > 1 && areStringsInsensitivelyEqual(fRelationships[0], objectName)
    ? fRelationships.slice(1)
    : [...fRelationships];

  const tryLoadFieldRelationship = async (index, objectName, parentRelationshipNames) => {
    const relationshipName = fieldRelationshipNames[index];
    const isNotLastRelationship = index < fieldRelationshipNames.length - 1;
    const basicData = {
      name: relationshipName, objectName, parentRelationshipNames,
      type: isNotLastRelationship ? RelationshipType.Relationship : relationshipType,
    };

    if (!objectLoader) {
      const relationshipNames = [...parentRelationshipNames, relationshipName];
      return [
        basicData, 
        ...(isNotLastRelationship ? await tryLoadFieldRelationship(index + 1, objectName, relationshipNames) : [])
      ];
    }

    const { fields: rawFields, childRelationships: rawChildRelationships, name: trueObjectName } = await objectLoader(objectName);
    Object.assign(basicData, { rawFields, rawChildRelationships, objectName: trueObjectName });

    if (isNotLastRelationship) {
      const fieldIndex = rawFields.findIndex(x => areStringsInsensitivelyEqual(relationshipName, x.relationshipName));
      if (fieldIndex === -1) {
        return null;
      }

      const rawField = rawFields[fieldIndex];
      const { relationshipName: trueRelationshipName } = rawField;
      const relationshipNames = [...parentRelationshipNames, trueRelationshipName]; 
      const isPolymorphic = isPolymorphicField(rawField);

      if (isPolymorphic) {
        // For 'SELECT Who.Type FROM Task 'Type' is an artificial SF field, which contains type of an object
        // of the 'Who' polymorphic relationship
        const nextRelationshipName = fieldRelationshipNames[index + 1];
        if (areStringsInsensitivelyEqual(nextRelationshipName, 'type')) {
          return [{ ...basicData, columnType: ColumnType.PolymorphicRelationshipType }];
        }
      }

      for (const referenceTo of rawField.referenceTo) {
        // For polymorphic relationships we don't know which relationship (object) is correct
        // Therefore try to match child relationships to all, if any fails - try next
        const nextRelationships = await tryLoadFieldRelationship(index + 1, referenceTo, relationshipNames);
        if (nextRelationships) {
          return [
            { ...basicData, name: trueRelationshipName, rawField, fieldIndex, referenceTo, isPolymorphic, },
            ...nextRelationships,
          ];
        }
      } 

      // Bad query
      return null;
    }

    if (RelationshipType.Field === relationshipType) {
      const fieldIndex = rawFields.findIndex(x => areStringsInsensitivelyEqual(relationshipName, x.name));
      if (fieldIndex === -1) {
        return null;
      }

      const rawField = rawFields[fieldIndex];
      const { name, label } = rawField;
      return [{ ...basicData, columnType: ColumnType.Field, rawField, fieldIndex, name, label }];
    }
        
    // RelationshipType.ChildRelationship
    const childRelationshipIndex = rawChildRelationships.findIndex(x => areStringsInsensitivelyEqual(relationshipName, x.relationshipName));
    if (childRelationshipIndex === -1) {
      return null;
    }    

    const rawChildRelationship = rawChildRelationships[childRelationshipIndex];
    const { relationshipName: name, childSObject: referenceTo } = rawChildRelationship;

    return [{ ...basicData, rawChildRelationship, childRelationshipIndex, name, referenceTo, }];
  }

  return await tryLoadFieldRelationship(0, objectName, pRelationships);
}
